const DECOPOLIS_URL = "https://decopolis.net/pages/fablerealm-bookstore";

const SHOW_PARAGRAPHS = [
  "Zan-o-Land tells the story of a forgotten American carnival town through thirty-six sculptural artifacts and the strange tales they contain.",
  "From the turn of the century through the early 1930s, Zan-o-Land was a community built around a carnival that came alive each night. Clowns, sideshow performers, craftsmen, merchants and ordinary families lived and worked alongside one another, creating a peculiar little world filled with invention, spectacle, friendship, love and loss.",
  "Hard times eventually brought the carnival to an end. Its people scattered, the midway disappeared, and Zan-o-Land slowly faded away.",
  "Its stories are inspired by the performers, dreamers and working families of early twentieth-century America. Zan-o-Land is ultimately the story of a place that disappeared, and the strange objects and stories it left behind.",
];

const BOOK_PARAGRAPH_1 =
  "Filled with colorful photographs, aged historical imagery and richly imagined tales, ZAN-O-LAND: The Last Relics of an American Carnival brings the mysterious world of Zan-o-Land to life. The book features 36 original sculptural artifacts, each accompanied by its own unique story revealing the eccentric characters, peculiar inventions and curious happenings of a forgotten carnival town. Equal parts art book, fictional history and carnival curiosity, it takes readers on a journey into a world filled with invention, humor, friendship, love and loss, and the strange relics left behind when the carnival disappeared.";

const BookText = ({ className }) => (
  <div className={className}>
    <p>{BOOK_PARAGRAPH_1}</p>
    <p className="mt-[26px]">
      Books will be available for purchase at{" "}
      <a
        href={DECOPOLIS_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="book-store-link"
        className="underline decoration-[#4E8A63]/50 underline-offset-2 transition-colors duration-300 hover:text-[#4E8A63]"
      >
        Decopolis FableRealms bookstore
      </a>{" "}
      in Tulsa, Oklahoma.
    </p>
  </div>
);

const HEADING = "text-[29px] font-bold uppercase leading-[40px] tracking-[0.15em] text-[#4E8A63]";
const BODY = "text-[15px] leading-[23.5px] text-[#2B2822]";
const LABEL = "text-[17px] font-bold uppercase leading-[20px] tracking-[0.04em] text-[#2B2822]";
const VALUE = "text-[15px] uppercase leading-[20px] tracking-[0.03em] text-[#2B2822]";

const PARCHMENT_STYLE = {
  backgroundImage: "url('/assets/parchment-bg.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "top center",
};

const EventDetails = ({ compact }) => {
  const label = compact ? "text-[15px] font-bold uppercase tracking-[0.04em] text-[#2B2822]" : LABEL;
  const value = compact ? "mt-[2px] text-[14px] uppercase tracking-[0.03em] text-[#2B2822]" : `mt-[3px] ${VALUE}`;
  const gap = compact ? "mt-5" : "mt-[20px]";
  return (
    <dl>
      <dt className={label}>Date:</dt>
      <dd data-testid="event-date" className={value}>NOV. 13 – DEC. 4</dd>
      <dt className={`${label} ${gap}`}>Location:</dt>
      <dd data-testid="event-location" className={value}>
        <a
          href={DECOPOLIS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="event-location-link"
          className="underline decoration-[#4E8A63]/50 underline-offset-2 transition-colors duration-300 hover:text-[#4E8A63]"
        >
          DECOPOLIS FABLEREALMS BOOKSTORE
        </a>
        <br />
        5729 E 11TH ST, TULSA, OK 74112
      </dd>
      <dt className={`${label} ${gap}`}>Opening Night Party:</dt>
      <dd data-testid="event-opening" className={value}>NOV. 13 6-9PM</dd>
    </dl>
  );
};

export const StoryBook = () => (
  <section data-testid="story-section" className="bg-parchment-mid" style={PARCHMENT_STYLE}>
    <h1 className="sr-only">Zan-o-Land — The Last Relics of an American Carnival</h1>

    {/* Desktop — exact 1440px PDF canvas (page y 722–1698) */}
    <div className="canvas-1440 relative hidden h-[976px] lg:block">
      <img src="/assets/side-label.png" alt="" aria-hidden="true" className="absolute left-0 top-[48px] w-[140px]" />
      <img
        src="/assets/title.png"
        alt="Zan-o-Land — The Last Relics of an American Carnival"
        data-testid="zanoland-title"
        className="absolute left-[172px] top-[51px] w-[539px]"
      />
      <div aria-hidden="true" className="absolute left-[158px] top-[186px] h-[4px] w-[552px] bg-[#85A697]" />

      <img
        src="/assets/stamp.png"
        alt="Hand-made, one-of-a-kind — B. Cooper Sculptures stamp"
        data-testid="handmade-stamp"
        className="absolute left-[184px] top-[244px] w-[142px]"
      />
      <div className="absolute left-[412px] top-[222px]">
        <EventDetails />
      </div>

      <h2 data-testid="show-heading" className={`absolute left-[767px] top-[41px] ${HEADING}`}>
        The Show
      </h2>
      <div className="absolute left-[767px] top-[93px] w-[544px] space-y-[27px]">
        {SHOW_PARAGRAPHS.map((p, i) => (
          <p key={i} className={BODY}>{p}</p>
        ))}
      </div>

      <div aria-hidden="true" className="absolute left-[145px] top-[482px] h-[4px] w-[1170px] bg-[#85A697]" />

      <h2 data-testid="book-heading" className={`absolute left-[195px] top-[532px] ${HEADING}`}>
        The Book
      </h2>
      <BookText className={`absolute left-[193px] top-[587px] w-[508px] ${BODY}`} />
      <img
        src="/assets/book.jpg"
        alt="ZAN-O-LAND: The Last Relics of an American Carnival — book on red velvet"
        data-testid="book-photo"
        loading="lazy"
        className="absolute left-[767px] top-[535px] w-[542px]"
      />
    </div>

    {/* Mobile / tablet — stacked */}
    <div className="px-6 py-10 sm:px-8 lg:hidden">
      <img src="/assets/title.png" alt="Zan-o-Land — The Last Relics of an American Carnival" className="w-full max-w-[420px]" />
      <div aria-hidden="true" className="mt-4 h-[3px] w-52 bg-[#85A697]" />
      <div className="mt-8 flex items-start gap-6">
        <img src="/assets/stamp.png" alt="Hand-made, one-of-a-kind — B. Cooper Sculptures stamp" className="w-24 shrink-0 sm:w-28" />
        <EventDetails compact />
      </div>

      <h2 className="mt-12 text-xl font-bold uppercase tracking-[0.15em] text-[#4E8A63]">The Show</h2>
      <div className="mt-4 space-y-5 text-[14px] leading-[1.75] text-[#2B2822]">
        {SHOW_PARAGRAPHS.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <div aria-hidden="true" className="my-12 h-[3px] w-full bg-[#85A697]" />

      <h2 className="text-xl font-bold uppercase tracking-[0.15em] text-[#4E8A63]">The Book</h2>
      <BookText className="mt-4 space-y-5 text-[14px] leading-[1.75] text-[#2B2822]" />
      <img
        src="/assets/book.jpg"
        alt="ZAN-O-LAND: The Last Relics of an American Carnival — book on red velvet"
        loading="lazy"
        className="mt-8 w-full"
      />
    </div>
  </section>
);
